import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, ExternalLink, FileText, Loader2, Trash2 } from "lucide-react";
import {
  deleteDevisSigne,
  getDevisSigneSignedUrl,
  isDevisSignePdfPath,
} from "../../lib/devisSigneUpload";

interface SignedQuoteViewerProps {
  jobId: string;
  storagePath?: string | null;
  canDelete?: boolean;
  onRemoved?: (storagePath: string) => void | Promise<void>;
  disabled?: boolean;
  /** Affiche le titre « Documents » au-dessus de la carte (désactivé en liste). */
  showTitle?: boolean;
}

export function SignedQuoteViewer({
  jobId,
  storagePath,
  canDelete = false,
  onRemoved,
  disabled = false,
  showTitle = false,
}: SignedQuoteViewerProps) {
  const { t } = useTranslation();
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const path = storagePath?.trim() || "";
  const isPdf = path ? isDevisSignePdfPath(path) : false;
  const fileName = path.split("/").pop() || path;

  const loadUrl = useCallback(async () => {
    if (!path) {
      setSignedUrl(null);
      setLoadError(null);
      return;
    }
    if (!path.startsWith(`${jobId}/`)) {
      setSignedUrl(null);
      setLoadError(t("signedQuote.invalidPath"));
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const url = await getDevisSigneSignedUrl(path);
      if (!url) {
        setSignedUrl(null);
        setLoadError(t("signedQuote.loadError"));
        return;
      }
      setSignedUrl(url);
    } catch {
      setSignedUrl(null);
      setLoadError(t("signedQuote.loadError"));
    } finally {
      setLoading(false);
    }
  }, [path, jobId, t]);

  useEffect(() => {
    void loadUrl();
  }, [loadUrl]);

  const handleDelete = async () => {
    if (!path || !canDelete || disabled || deleting) return;
    if (!window.confirm(t("signedQuote.deleteConfirm"))) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      const { error: storageErr } = await deleteDevisSigne(path);
      if (storageErr) {
        setDeleteError(storageErr);
        return;
      }
      await onRemoved?.(path);
      setSignedUrl(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t("signedQuote.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  if (!path) return null;

  return (
    <div className="rounded-2xl border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-950/20 p-4 space-y-3">
      {showTitle && (
        <p className="text-sm font-semibold text-gray-900 dark:text-white">{t("signedQuote.title")}</p>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
          {t("signedQuote.loading")}
        </div>
      )}

      {loadError && !loading && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{loadError}</span>
        </div>
      )}

      {signedUrl && !loading && (
        <div className="space-y-3">
          {isPdf ? (
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800/80 px-4 py-3">
              <FileText className="h-10 w-10 text-red-600 dark:text-red-400 shrink-0" aria-hidden />
              <p className="text-sm text-gray-700 dark:text-slate-200 truncate flex-1">{fileName}</p>
            </div>
          ) : (
            <a href={signedUrl} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden border border-gray-200 dark:border-white/10">
              <img src={signedUrl} alt={t("signedQuote.previewAlt")} className="w-full max-h-64 object-contain bg-white dark:bg-slate-900" />
            </a>
          )}
          <div className="flex flex-wrap gap-2">
            <a
              href={signedUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2.5 transition-colors"
            >
              <ExternalLink size={16} aria-hidden />
              {isPdf ? t("signedQuote.openPdf") : t("signedQuote.openImage")}
            </a>
            {canDelete && (
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={disabled || deleting}
                className="inline-flex items-center gap-2 rounded-xl border border-red-200 dark:border-red-500/40 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 text-sm font-medium px-4 py-2.5 hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-50 transition-colors"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Trash2 size={16} aria-hidden />
                )}
                {t("signedQuote.delete")}
              </button>
            )}
          </div>
        </div>
      )}

      {deleteError && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-300">
          {deleteError}
        </p>
      )}
    </div>
  );
}
